import CommonModal from "@components/common/CommonModal";
import { checkFormChanges } from "@lib/checkFormChanges";
import { filterByLabel } from "@lib/filterByLabel";
import { DropdownService } from "@/services/APIs";
import { WorkflowState } from "@/utils/types/workflowMain";
import { useQuery } from "@tanstack/react-query";
import { Col, Form, Input, InputNumber, Row, Select } from "antd";
import { useEffect } from "react";
import { notify } from "../common/Notification";

const { TextArea } = Input;

interface WorkflowStateModalProps {
  open: boolean;
  onCancel: () => void;
  onSubmit: (data: WorkflowState) => void;
  initialData?: WorkflowState | null;
  isEditMode?: boolean;
  disabled?: boolean;
  confirmLoading?: boolean;
}

const WorkflowStateModal: React.FC<WorkflowStateModalProps> = ({
  open,
  onCancel,
  onSubmit,
  initialData,
  isEditMode = false,
  disabled = false,
  confirmLoading = false,
}) => {
  const [form] = Form.useForm();
  // Data fetching queries
  const { data: stateOptions, isLoading: stateLoading } = useQuery({
    queryKey: ["workflow_state_master"],
    queryFn: () => DropdownService.getDropdownData("workflow_state_master"),
  });

  const { data: actionOptions, isLoading: actionLoading } = useQuery({
    queryKey: ["workflow_action"],
    queryFn: () => DropdownService.getDropdownData("workflow_action"),
  });

  const { data: userOptions, isLoading: userLoading } = useQuery({
    queryKey: ["user"],
    queryFn: () => DropdownService.getDropdownData("user"),
  });

  const { data: roleOptions, isLoading: roleLoading } = useQuery({
    queryKey: ["TenantRole"],
    queryFn: () => DropdownService.getDropdownData("TenantRole"),
  });

  // Reset form when modal opens/closes or initial data changes
  useEffect(() => {
    if (open) {
      if (initialData) {
        // Set form fields with initial data
        form.setFieldsValue({
          state_id: initialData.state_id,
          action_id: initialData.action_id,
          next_state_id: initialData.next_state_id,
          role_id: initialData.role_id ?? undefined,
          user_id: initialData.user_id ?? undefined,
          due_day: initialData.due_day,
          condition: initialData.condition ?? "",
        });
      } else {
        form.resetFields();
      }
    }
  }, [open, initialData, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (
        isEditMode &&
        initialData &&
        !checkFormChanges(values, initialData, notify)
      ) {
        return;
      }
      // Clean the data
      const data = {
        state_id: values.state_id,
        action_id: values.action_id,
        next_state_id: values.next_state_id,
        role_id: values.role_id ?? "",
        user_id: values.user_id ?? "",
        due_day: values.due_day,
        condition: values.condition ?? "",
      };

      onSubmit(data);
      form.resetFields();
    } catch (error: any) {
      console.error("Failed to submit form:", error);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  // Handle user selection change
  const handleUserChange = (value: any) => {
    if (value) {
      form.setFieldsValue({ role_id: undefined });
    }
  };

  // Handle role selection change
  const handleRoleChange = (value: any) => {
    if (value) {
      form.setFieldsValue({ user_id: undefined });
    }
  };

  return (
    <CommonModal
      title={isEditMode ? "Edit State Transition" : "Add State Transition"}
      open={open}
      onOk={handleSubmit}
      onCancel={handleCancel}
      width={800}
      okText={isEditMode ? "Update" : "Add"}
      cancelText="Cancel"
      confirmLoading={confirmLoading}
      destroyOnClose
    >
      <Form form={form} layout="vertical">
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name="state_id"
              label="Current State"
              rules={[
                { required: true, message: "Please select current state" },
              ]}
            >
              <Select
                placeholder="Select current state"
                options={stateOptions}
                loading={stateLoading}
                showSearch
                filterOption={filterByLabel}
                disabled={disabled}
              />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item
              name="action_id"
              label="Action"
              rules={[{ required: true, message: "Please select action" }]}
            >
              <Select
                placeholder="Select action"
                options={actionOptions}
                loading={actionLoading}
                showSearch
                filterOption={filterByLabel}
                disabled={disabled}
              />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item
              name="next_state_id"
              label="Next State"
              rules={[{ required: true, message: "Please select next state" }]}
            >
              <Select
                placeholder="Select next state"
                options={stateOptions}
                loading={stateLoading}
                showSearch
                filterOption={filterByLabel}
                disabled={disabled}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name="role_id"
              label="Role"
              rules={[
                {
                  validator: async (_, value) => {
                    const userId = form.getFieldValue("user_id");
                    if (!value && !userId) {
                      throw new Error("Please select either a user or a role");
                    }
                    if (value && userId) {
                      throw new Error(
                        "Please select either a user OR a role, not both"
                      );
                    }
                  },
                },
              ]}
            >
              <Select
                placeholder="Select role"
                options={roleOptions}
                loading={roleLoading}
                allowClear
                onChange={handleRoleChange}
                showSearch
                filterOption={filterByLabel}
                disabled={disabled}
              />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item
              name="user_id"
              label="User"
              rules={[
                {
                  validator: async (_, value) => {
                    const roleId = form.getFieldValue("role_id");
                    if (!value && !roleId) {
                      throw new Error("Please select either a user or a role");
                    }
                    if (value && roleId) {
                      throw new Error(
                        "Please select either a user OR a role, not both"
                      );
                    }
                  },
                },
              ]}
            >
              <Select
                placeholder="Select user"
                options={userOptions}
                loading={userLoading}
                allowClear
                onChange={handleUserChange}
                showSearch
                filterOption={filterByLabel}
                disabled={disabled}
              />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item
              name="due_day"
              label="Due Days"
              rules={[
                { required: true, message: "Please enter due days" },
                {
                  type: "number",
                  min: 1,
                  message: "Due days must be at least 1",
                },
                {
                  type: "number",
                  max: 365,
                  message: "Due days cannot exceed 365",
                },
              ]}
            >
              <InputNumber
                placeholder="Enter due days"
                min={1}
                max={365}
                style={{ width: "100%" }}
                disabled={disabled}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              name="condition"
              label="Condition (Optional)"
              tooltip="Enter conditional logic to determine if this transition should be executed. Example: self.first_name == 'John'"
            >
              <TextArea
                placeholder="Enter conditional logic (e.g., self.first_name == 'John')"
                rows={3}
                showCount
                maxLength={500}
                autoSize={{ minRows: 4, maxRows: 6 }}
                disabled={disabled}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </CommonModal>
  );
};

export default WorkflowStateModal;
